import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'
import { toast } from 'react-toastify'

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'needs_work', label: 'Needs Work', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'poor', label: 'Poor', color: 'bg-red-100 text-red-800 border-red-300' },
]

const RESPONSIBILITY_OPTIONS = [
  { value: 'wtf', label: 'WTF Team', color: 'bg-blue-100 text-blue-800' },
  { value: 'owner', label: 'Gym Owner', color: 'bg-purple-100 text-purple-800' },
]

function ChecklistItem({ item, onUpdate, onUpload, onDeleteMedia, checklistStatus }) {
  const [notes, setNotes] = useState(item.notes || '')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const isLocked = checklistStatus === 'approved'

  const handleCondition = async (condition) => {
    if (isLocked) return
    setSaving(true)
    await onUpdate(item.id, { condition })
    setSaving(false)
  }

  const handleResponsibility = async (responsibility) => {
    if (isLocked) return
    setSaving(true)
    await onUpdate(item.id, { responsibility })
    setSaving(false)
  }

  const handleNotesBlur = async () => {
    if (isLocked || notes === item.notes) return
    setSaving(true)
    await onUpdate(item.id, { notes })
    setSaving(false)
  }

  const handleFileChange = async (e) => {
    if (!e.target.files.length || isLocked) return
    setUploading(true)
    await onUpload(item.id, e.target.files)
    setUploading(false)
    e.target.value = ''
  }

  const filled = item.condition !== null

  return (
    <div className={`border rounded-lg p-4 ${filled ? 'border-gray-200 bg-white' : 'border-orange-200 bg-orange-50'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900">{item.title}</h4>
          {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
        </div>
        {saving && <span className="text-xs text-gray-400">Saving...</span>}
        {filled && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Filled</span>}
      </div>

      <div className="mb-3">
        <p className="text-xs font-medium text-gray-600 mb-1">Condition *</p>
        <div className="flex gap-2 flex-wrap">
          {CONDITION_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => handleCondition(opt.value)}
              disabled={isLocked}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                item.condition === opt.value
                  ? opt.color + ' border-current font-bold ring-2 ring-offset-1'
                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
              } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {(item.condition === 'needs_work' || item.condition === 'poor') && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-600 mb-1">Who fixes this? *</p>
          <div className="flex gap-2">
            {RESPONSIBILITY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleResponsibility(opt.value)}
                disabled={isLocked}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  item.responsibility === opt.value
                    ? opt.color + ' ring-2 ring-offset-1 ring-current font-bold'
                    : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-gray-400'
                } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3">
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          disabled={isLocked}
          placeholder="Add notes (optional)..."
          rows={2}
          className="w-full text-xs border border-gray-200 rounded p-2 resize-none focus:outline-none focus:border-blue-400 disabled:bg-gray-50"
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs font-medium text-gray-600">Photos / Videos</p>
          {!isLocked && (
            <label className="cursor-pointer text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-0.5 rounded transition-colors">
              {uploading ? 'Uploading...' : '+ Add'}
              <input type="file" accept="image/*,video/*" multiple onChange={handleFileChange} className="hidden" disabled={uploading} />
            </label>
          )}
        </div>
        {item.media && item.media.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {item.media.map(m => (
              <div key={m.id} className="relative group">
                {m.mimetype?.startsWith('video') ? (
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">Video</div>
                ) : (
                  <img src={m.url} alt="" className="w-16 h-16 object-cover rounded border border-gray-200" onError={e => { e.target.style.display='none' }} />
                )}
                {!isLocked && (
                  <button
                    onClick={() => onDeleteMedia(item.id, m.id)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs hidden group-hover:flex items-center justify-center"
                  >×</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function PreWorkChecklistPage() {
  const { gymId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [gymName, setGymName] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [activeCategory, setActiveCategory] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  const isCEO = user?.role?.level === 1

  const load = useCallback(async () => {
    try {
      const [checklistRes, gymRes] = await Promise.all([
        axios.get(`/api/pre-work-checklist/${gymId}`),
        axios.get(`/api/gyms/${gymId}`),
      ])
      setData(checklistRes.data)
      setGymName(gymRes.data.gym?.name || gymRes.data.name || '')
      if (!activeCategory) {
        const categories = [...new Set(checklistRes.data.checklist.items.map(i => i.category))]
        setActiveCategory(categories[0] || null)
      }
    } catch (err) {
      toast.error('Failed to load checklist')
    } finally {
      setLoading(false)
    }
  }, [gymId])

  useEffect(() => { load() }, [load])

  const handleUpdate = async (itemId, updates) => {
    try {
      const res = await axios.put(`/api/pre-work-checklist/${gymId}/items/${itemId}`, updates)
      setData(prev => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          items: prev.checklist.items.map(i => i.id === itemId ? { ...i, ...res.data } : i),
        },
      }))
    } catch (err) {
      toast.error('Failed to save')
    }
  }

  const handleUpload = async (itemId, files) => {
    const formData = new FormData()
    Array.from(files).forEach(f => formData.append('files', f))
    try {
      const res = await axios.post(`/api/pre-work-checklist/${gymId}/items/${itemId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setData(prev => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          items: prev.checklist.items.map(i =>
            i.id === itemId ? { ...i, media: [...(i.media || []), ...res.data.media] } : i
          ),
        },
      }))
      toast.success(`${res.data.uploaded} file(s) uploaded`)
    } catch (err) {
      toast.error('Upload failed')
    }
  }

  const handleDeleteMedia = async (itemId, mediaId) => {
    try {
      await axios.delete(`/api/pre-work-checklist/${gymId}/items/${itemId}/photos/${mediaId}`)
      setData(prev => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          items: prev.checklist.items.map(i =>
            i.id === itemId ? { ...i, media: (i.media || []).filter(m => m.id !== mediaId) } : i
          ),
        },
      }))
    } catch (err) {
      toast.error('Failed to delete')
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await axios.post(`/api/pre-work-checklist/${gymId}/submit`)
      toast.success('Checklist submitted for CEO approval!')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async () => {
    try {
      await axios.post(`/api/pre-work-checklist/${gymId}/approve`)
      toast.success('Pre-Work Checklist approved! Agreement Signed stage is now unlocked.')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve')
    }
  }

  const handleReject = async () => {
    try {
      await axios.post(`/api/pre-work-checklist/${gymId}/reject`, { reason: rejectReason })
      toast.success('Checklist rejected. Team has been notified to revise.')
      setShowRejectForm(false)
      setRejectReason('')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject')
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Loading checklist...</div>
  if (!data) return <div className="p-8 text-center text-red-500">Failed to load checklist</div>

  const { checklist, stats } = data
  const categories = [...new Set(checklist.items.map(i => i.category))]
  const categoryItems = checklist.items.filter(i => i.category === activeCategory)
  const catStats = stats.byCategory[activeCategory] || {}
  const isLocked = checklist.status === 'approved'
  const statusColors = {
    in_progress: 'bg-yellow-100 text-yellow-800',
    submitted: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate(`/gyms/${gymId}`)} className="text-sm text-blue-600 hover:underline mb-1">← Back to {gymName}</button>
          <h1 className="text-2xl font-bold text-gray-900">Pre-Work Checklist</h1>
          <p className="text-gray-500 text-sm">{gymName} — Capture current gym condition before acquisition</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[checklist.status]}`}>
          {checklist.status === 'in_progress' ? 'In Progress' : checklist.status === 'submitted' ? 'Awaiting CEO Approval' : checklist.status === 'approved' ? 'Approved ✓' : 'Rejected — Needs Revision'}
        </span>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{stats.filled} / {stats.total} items filled ({stats.percent}%)</span>
          <span className="text-sm text-gray-500">{stats.total - stats.filled} remaining</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${stats.percent === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${stats.percent}%` }}
          />
        </div>
        {checklist.status === 'rejected' && checklist.rejectionReason && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            <strong>CEO Rejection Note:</strong> {checklist.rejectionReason}
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <div className="w-48 flex-shrink-0">
          <div className="bg-white rounded-lg border overflow-hidden">
            {categories.map(cat => {
              const cs = stats.byCategory[cat] || { total: 0, filled: 0 }
              const done = cs.filled === cs.total
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 transition-colors ${
                    activeCategory === cat ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{cat}</span>
                    {done ? <span className="text-green-500 text-xs">✓</span> : <span className="text-xs text-gray-400">{cs.filled}/{cs.total}</span>}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800">{activeCategory}</h2>
            <span className="text-sm text-gray-500">{catStats.filled || 0} / {catStats.total || 0} filled</span>
          </div>
          <div className="space-y-3">
            {categoryItems.map(item => (
              <ChecklistItem
                key={item.id}
                item={item}
                onUpdate={handleUpdate}
                onUpload={handleUpload}
                onDeleteMedia={handleDeleteMedia}
                checklistStatus={checklist.status}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg border p-4 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {stats.percent < 100 ? `${stats.total - stats.filled} items still need condition ratings` : 'All items filled — ready to submit'}
        </div>
        <div className="flex gap-3">
          {!isCEO && (checklist.status === 'in_progress' || checklist.status === 'rejected') && (
            <button
              onClick={handleSubmit}
              disabled={submitting || stats.percent < 100}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit for CEO Approval'}
            </button>
          )}
          {isCEO && checklist.status === 'submitted' && (
            <>
              <button
                onClick={() => setShowRejectForm(!showRejectForm)}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50"
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
              >
                Approve Checklist
              </button>
            </>
          )}
        </div>
      </div>

      {showRejectForm && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-700 mb-2">Rejection reason (required)</p>
          <textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Describe what needs to be fixed or re-inspected..."
            rows={3}
            className="w-full text-sm border border-red-200 rounded p-2 focus:outline-none focus:border-red-400"
          />
          <div className="flex gap-2 mt-2">
            <button onClick={handleReject} disabled={rejectReason.trim().length < 5} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm disabled:opacity-50">
              Confirm Rejection
            </button>
            <button onClick={() => setShowRejectForm(false)} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
